import React, { useEffect, useState } from 'react';
import { loadingState } from 'src/atom/LoadingState';
import { useRecoilState } from 'recoil';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { verifyUserID } from 'src/services/Login/ForgotPassword';
import LogoHeader from 'src/components/header/LogoHeader';

const ForgotPassword = () => {
  const [, setShowResendButton] = useState(false);
  const [, enableLoader] = useRecoilState(loadingState);
  const [countdown, setCountdown] = useState(120);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const userObject = {
    userId: '',
  };
  const [formData, setFormData] = useState(userObject);
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({
    userId: '',
  });

  const validateForm = () => {
    let errors = {};
    let isValid = true;
    if (!formData.userId) {
      errors.userId = 'Please provide User ID';
      isValid = false;
    }
    setValidationErrors(errors);
    return isValid;
  };

  const handleUserIdOnchange = (event) => {
    const value = event.target.value;
    setFormData((prevFormData) => ({
      ...prevFormData,
      userId: value,
    }));
  };

  const handleProceed = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    enableLoader(true);
      validateForm();
    setIsSubmitting(true);
    try {
      const response = await verifyUserID(formData.userId);
      if (response.success === true) {
        const bodyValues = Object.values(response.body);
        navigate('/validate-password', {
          state: { token: bodyValues, userId: formData.userId },
        });
      } else {
        toast.error(response.message, {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
      }
    } catch (error) {
      toast.error(error.message, {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } finally {
      enableLoader(false);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let timer = null;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else {
      setShowResendButton(true);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  return (
    <>
      <div className="card">
        <LogoHeader />
      </div>
      <div className="container">
        <div className="custom-box">
          <div className="card ms-3 me-3 mt-3">
            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  <strong className="password-heading">Reset Password</strong>
                </div>
              </div>
              <hr />
              <div className="row mt-2">
                <div className="col-12">
                  <div className="d-flex flex-column align-items-start">
                    <label className="form-label">
                      Enter your User ID to receive an OTP
                    </label>
                  </div>
                  <form onSubmit={handleProceed}>
                    <div className="row justify-content-center">
                      <span className="text-danger">{success}</span>
                      <div className="col-12 d-flex justify-content-center align-items-center">
                        <input
                          type="text"
                          className={`form-control ${validationErrors.userId ? 'is-invalid' : ''}`}
                          id="userId"
                          maxLength="14"
                          pattern="^(?!$)[a-zA-Z0-9_]+$"
                          placeholder="Enter User ID"
                          value={formData.userId}
                          onChange={handleUserIdOnchange}
                        />
                        {validationErrors.userId && (
                          <div className="invalid-feedback">{validationErrors.userId}</div>
                        )}
                        <button
                          className="ms-2 btn btn-primary"
                          type="submit"
                          disabled={!formData.userId || isSubmitting}
                        >
                          {isSubmitting ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                      </div>
                    </div>
                  </form>
                  <div className="toolbar mt-2">
                    <span>
                      <Link to="/login" className="back-link">
                        Back to Login
                      </Link>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 70vh;
            background-color: #f8f9fa;
            padding: 1rem;
          }

          .custom-box {
            width: 100%;
            max-width: 600px;
            background-color: #ffffff;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            padding: 1.5rem;
          }

          .card {
            border: none;
            border-radius: 8px;
            background-color: transparent;
          }

          .password-heading {
            display: flex;
            justify-content: start;
            align-items: center;
            font-size: 1.25rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }

          .form-label {
            color: #dc3545;
            font-size: 0.9rem;
            margin-bottom: 1rem;
          }

          .form-control {
            border-radius: 4px;
            padding: 0.5rem 0.75rem;
            font-size: 1rem;
            height: 38px; /* Explicit height for consistency */
          }

          .btn-primary {
            border-radius: 4px;
            padding: 0.5rem 1.5rem;
            font-size: 1rem;
            height: 38px; /* Match input height */
            line-height: 1.5; /* Ensure text is vertically centered */
            transition: background-color 0.2s ease;
          }

          .btn-primary:hover {
            background-color: #0056b3;
          }

          .toolbar {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 1rem;
          }

          .back-link {
            text-decoration: none;
            color: #007bff;
            font-weight: 500;
            transition: color 0.2s ease;
          }

          .back-link:hover {
            color: #0056b3;
            text-decoration: underline;
          }

          @media (max-width: 576px) {
            .custom-box {
              padding: 1rem;
            }

            .form-control,
            .btn-primary {
              font-size: 0.9rem;
              height: 34px; /* Slightly smaller for mobile */
            }
          }
        `}
      </style>
      <ToastContainer />
    </>
  );
};

export default ForgotPassword;